import { useContext } from 'react';
import { ThemeContext } from '../contexts/theme-context';
import { DEFAULT_THEME } from '../types/theme';
// Base spacing values (in rem)
const BASE_SPACING = {
    xs: 0.5, // 8px
    sm: 1, // 16px
    md: 1.5, // 24px
    lg: 2, // 32px
    xl: 3, // 48px
    '2xl': 4, // 64px
    '3xl': 6, // 96px
    '4xl': 8, // 128px
};
export function useSpacing() {
    // Use context directly to avoid error when no provider
    const context = useContext(ThemeContext);
    const theme = context?.theme || DEFAULT_THEME;
    // Get spacing scale from theme
    const scale = theme.spacingMode === 'compact' ? 0.75 :
        theme.spacingMode === 'spacious' ? 1.5 : 1;
    // Function to get computed spacing value
    const spacing = (value) => {
        const baseValue = BASE_SPACING[value];
        const computedValue = baseValue * scale;
        return `${computedValue}rem`;
    };
    // Utility classes based on spacing mode
    const classes = {
        // Padding classes
        p: {
            xs: theme.spacingMode === 'compact' ? 'p-1' : theme.spacingMode === 'spacious' ? 'p-3' : 'p-2',
            sm: theme.spacingMode === 'compact' ? 'p-2' : theme.spacingMode === 'spacious' ? 'p-6' : 'p-4',
            md: theme.spacingMode === 'compact' ? 'p-3' : theme.spacingMode === 'spacious' ? 'p-8' : 'p-6',
            lg: theme.spacingMode === 'compact' ? 'p-4' : theme.spacingMode === 'spacious' ? 'p-12' : 'p-8',
            xl: theme.spacingMode === 'compact' ? 'p-6' : theme.spacingMode === 'spacious' ? 'p-16' : 'p-12',
        },
        // Margin classes
        m: {
            xs: theme.spacingMode === 'compact' ? 'm-1' : theme.spacingMode === 'spacious' ? 'm-3' : 'm-2',
            sm: theme.spacingMode === 'compact' ? 'm-2' : theme.spacingMode === 'spacious' ? 'm-6' : 'm-4',
            md: theme.spacingMode === 'compact' ? 'm-3' : theme.spacingMode === 'spacious' ? 'm-8' : 'm-6',
            lg: theme.spacingMode === 'compact' ? 'm-4' : theme.spacingMode === 'spacious' ? 'm-12' : 'm-8',
            xl: theme.spacingMode === 'compact' ? 'm-6' : theme.spacingMode === 'spacious' ? 'm-16' : 'm-12',
        },
        // Gap classes for flex/grid
        gap: {
            xs: theme.spacingMode === 'compact' ? 'gap-1' : theme.spacingMode === 'spacious' ? 'gap-3' : 'gap-2',
            sm: theme.spacingMode === 'compact' ? 'gap-2' : theme.spacingMode === 'spacious' ? 'gap-6' : 'gap-4',
            md: theme.spacingMode === 'compact' ? 'gap-3' : theme.spacingMode === 'spacious' ? 'gap-8' : 'gap-6',
            lg: theme.spacingMode === 'compact' ? 'gap-4' : theme.spacingMode === 'spacious' ? 'gap-12' : 'gap-8',
            xl: theme.spacingMode === 'compact' ? 'gap-6' : theme.spacingMode === 'spacious' ? 'gap-16' : 'gap-12',
        },
        // Space between classes
        space: {
            xs: theme.spacingMode === 'compact' ? 'space-y-1' : theme.spacingMode === 'spacious' ? 'space-y-3' : 'space-y-2',
            sm: theme.spacingMode === 'compact' ? 'space-y-2' : theme.spacingMode === 'spacious' ? 'space-y-6' : 'space-y-4',
            md: theme.spacingMode === 'compact' ? 'space-y-3' : theme.spacingMode === 'spacious' ? 'space-y-8' : 'space-y-6',
            lg: theme.spacingMode === 'compact' ? 'space-y-4' : theme.spacingMode === 'spacious' ? 'space-y-12' : 'space-y-8',
            xl: theme.spacingMode === 'compact' ? 'space-y-6' : theme.spacingMode === 'spacious' ? 'space-y-16' : 'space-y-12',
        },
    };
    return {
        spacing,
        scale,
        mode: theme.spacingMode,
        classes,
    };
}
